import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";

type OnboardingState = {
	completedSteps: string[];
	currentStep: string | null;
	dismissed: boolean;
	completed: boolean;
	loading: boolean;
};

type OnboardingContextValue = OnboardingState & {
	completeStep: (stepId: string) => Promise<void>;
	skipStep: (stepId: string) => Promise<void>;
	dismiss: () => Promise<void>;
	resume: () => Promise<void>;
};

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function OnboardingProvider({ children }: { children: ReactNode }) {
	const [state, setState] = useState<OnboardingState>({
		completedSteps: [],
		currentStep: null,
		dismissed: false,
		completed: false,
		loading: true,
	});

	const fetchState = useCallback(async () => {
		try {
			const res = await fetch("/api/platform/onboarding", {
				credentials: "include",
			});
			if (!res.ok) return;
			const data = (await res.json()) as {
				completedSteps: string[];
				currentStep: string | null;
				dismissed: boolean;
				completed: boolean;
			};
			setState({
				completedSteps: data.completedSteps,
				currentStep: data.currentStep,
				dismissed: data.dismissed,
				completed: data.completed,
				loading: false,
			});
		} catch {
			// swallow network errors
		} finally {
			setState((prev) => ({ ...prev, loading: false }));
		}
	}, []);

	useEffect(() => {
		fetchState();
	}, [fetchState]);

	const patchOnboarding = useCallback(
		async (body: Record<string, string>) => {
			try {
				const res = await fetch("/api/platform/onboarding", {
					method: "PATCH",
					credentials: "include",
					headers: {
						"Content-Type": "application/json",
						Origin: window.location.origin,
					},
					body: JSON.stringify(body),
				});
				if (res.ok) {
					await fetchState();
				}
			} catch {
				// swallow
			}
		},
		[fetchState],
	);

	const completeStep = useCallback(
		async (stepId: string) => {
			await patchOnboarding({ action: "complete-step", stepId });
		},
		[patchOnboarding],
	);

	const skipStep = useCallback(
		async (stepId: string) => {
			await patchOnboarding({ action: "skip-step", stepId });
		},
		[patchOnboarding],
	);

	const dismiss = useCallback(async () => {
		await patchOnboarding({ action: "dismiss" });
	}, [patchOnboarding]);

	const resume = useCallback(async () => {
		await patchOnboarding({ action: "resume" });
	}, [patchOnboarding]);

	return (
		<OnboardingContext.Provider
			value={{
				...state,
				completeStep,
				skipStep,
				dismiss,
				resume,
			}}
		>
			{children}
		</OnboardingContext.Provider>
	);
}

export function useOnboarding() {
	const ctx = useContext(OnboardingContext);
	if (!ctx)
		throw new Error("useOnboarding must be used within OnboardingProvider");
	return ctx;
}
